import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { XStack, YStack } from "tamagui";

import type { KindredAnnouncement } from "@/lib/announcements";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminIconButton, AdminPageScaffold, AdminStatusPill } from "@/components/AdminScaffold";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppDialogSurface,
  AppEmptyState,
  AppFieldGroup,
  AppFormSection,
  AppInput,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppText,
  AppTextArea,
  kindredFontFamilies,
} from "../../ui-foundation";
import {
  getDefaultSupportCardCountryScope,
  getDefaultSupportCardTaxonomy,
  getSupportCardCountryScopeLabel,
  isSupportCard,
  SUPPORT_CARD_COUNTRY_SCOPE_OPTIONS,
} from "../../shared/support";
import {
  describeContentHealthCard,
  formatContentHealthDate,
  getContentHealthSummary,
} from "../../shared/system";

type ContentCardRecord = Database["public"]["Tables"]["content_cards"]["Row"];

type EditorState =
  | { type: "card"; value: ContentCardRecord | "new" }
  | { type: "announcement"; value: KindredAnnouncement | "new" }
  | null;

const emptyCardForm = {
  title: "",
  body: "",
  link_url: "",
  sort_order: 0,
  is_active: true,
  ...getDefaultSupportCardTaxonomy(),
  country_scope: getDefaultSupportCardCountryScope(),
  last_reviewed_at: "",
};

const emptyAnnouncementForm = {
  title: "",
  body: "",
  cta_label: "",
  cta_url_or_route: "",
  audience_scope: "all_signed_in",
  starts_at: "",
  ends_at: "",
  is_active: true,
  display_style: "banner",
  dismissible: true,
};

const toLocalDateTimeInput = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
};

const fromLocalDateTimeInput = (value: string) => (value ? new Date(value).toISOString() : null);

const audienceLabel = (value: string) => {
  switch (value) {
    case "admins_only":
      return "Admins only";
    case "moderators_only":
      return "Moderators only";
    case "members_only":
      return "Members only";
    case "all_signed_in":
    default:
      return "All signed-in users";
  }
};

const audienceOptions = [
  { value: "all_signed_in", label: "All signed-in users" },
  { value: "admins_only", label: "Admins only" },
  { value: "moderators_only", label: "Moderators only" },
  { value: "members_only", label: "Members only" },
];

// The generated Supabase client types in this repo do not yet include the announcements table.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const announcementsTable = "announcements" as any;

const AdminContent = () => {
  const [cards, setCards] = useState<ContentCardRecord[]>([]);
  const [announcements, setAnnouncements] = useState<KindredAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditorState>(null);
  const [cardForm, setCardForm] = useState(emptyCardForm);
  const [announcementForm, setAnnouncementForm] = useState(emptyAnnouncementForm);

  const fetchData = async () => {
    const [{ data: nextCards }, { data: nextAnnouncements }] = await Promise.all([
      supabase.from("content_cards").select("*").order("section").order("category").order("sort_order"),
      supabase.from(announcementsTable).select("*").order("updated_at", { ascending: false }),
    ]);

    setCards(nextCards || []);
    setAnnouncements((nextAnnouncements || []) as KindredAnnouncement[]);
    setLoading(false);
  };

  useEffect(() => {
    void fetchData();
  }, []);

  const openEditCard = (card: ContentCardRecord) => {
    setEditing({ type: "card", value: card });
    setCardForm({
      title: card.title,
      body: card.body,
      link_url: card.link_url || "",
      sort_order: card.sort_order,
      is_active: card.is_active,
      section: card.section,
      category: card.category,
      country_scope: card.country_scope,
      last_reviewed_at: card.last_reviewed_at || "",
    });
  };

  const openNewCard = () => {
    setEditing({ type: "card", value: "new" });
    setCardForm(emptyCardForm);
  };

  const openEditAnnouncement = (announcement: KindredAnnouncement) => {
    setEditing({ type: "announcement", value: announcement });
    setAnnouncementForm({
      title: announcement.title,
      body: announcement.body,
      cta_label: announcement.cta_label || "",
      cta_url_or_route: announcement.cta_url_or_route || "",
      audience_scope: announcement.audience_scope,
      starts_at: toLocalDateTimeInput(announcement.starts_at),
      ends_at: toLocalDateTimeInput(announcement.ends_at),
      is_active: announcement.is_active,
      display_style: announcement.display_style,
      dismissible: announcement.dismissible,
    });
  };

  const openNewAnnouncement = () => {
    setEditing({ type: "announcement", value: "new" });
    setAnnouncementForm(emptyAnnouncementForm);
  };

  const closeEditor = () => setEditing(null);

  const save = async () => {
    if (!editing) return;

    if (editing.type === "card") {
      const payload = {
        title: cardForm.title,
        body: cardForm.body,
        link_url: cardForm.link_url || null,
        sort_order: cardForm.sort_order,
        is_active: cardForm.is_active,
        section: cardForm.section,
        category: cardForm.category,
        country_scope: cardForm.country_scope,
        last_reviewed_at: cardForm.last_reviewed_at || null,
      };

      if (editing.value === "new") {
        await supabase.from("content_cards").insert(payload);
      } else {
        await supabase.from("content_cards").update(payload).eq("id", editing.value.id);
      }
    } else {
      const payload = {
        title: announcementForm.title,
        body: announcementForm.body,
        cta_label: announcementForm.cta_label || null,
        cta_url_or_route: announcementForm.cta_url_or_route || null,
        audience_scope: announcementForm.audience_scope,
        starts_at: fromLocalDateTimeInput(announcementForm.starts_at),
        ends_at: fromLocalDateTimeInput(announcementForm.ends_at),
        is_active: announcementForm.is_active,
        display_style: announcementForm.display_style,
        dismissible: announcementForm.dismissible,
      };

      if (editing.value === "new") {
        await supabase.from(announcementsTable).insert(payload);
      } else {
        await supabase.from(announcementsTable).update(payload).eq("id", editing.value.id);
      }
    }

    setEditing(null);
    toast.success("Saved");
    void fetchData();
  };

  const toggleCardActive = async (card: ContentCardRecord) => {
    await supabase.from("content_cards").update({ is_active: !card.is_active }).eq("id", card.id);
    void fetchData();
  };

  const toggleAnnouncementActive = async (announcement: KindredAnnouncement) => {
    await supabase.from(announcementsTable).update({ is_active: !announcement.is_active }).eq("id", announcement.id);
    void fetchData();
  };

  const supportCards = cards.filter((card) => isSupportCard(card));
  const helperCards = cards.filter((card) => card.section === "helper-packs");
  const contentHealth = useMemo(() => getContentHealthSummary(cards), [cards]);

  const openHealthCard = (cardId: string) => {
    const card = cards.find((item) => item.id === cardId);
    if (!card) return;
    openEditCard(card);
  };

  return (
    <AdminPageScaffold
      title="Content"
      subtitle="Manage support cards, helper packs, announcements, and annual review health from one shared admin screen."
      fallbackPath="/admin"
      action={<AdminIconButton icon={Plus} label="Add card" onPress={openNewCard} />}
    >
      {loading ? (
        <AppLoadingState
          title="Loading content"
          body="Pulling support cards, helper packs, announcements, and review health."
        />
      ) : (
        <>
          <AppCard>
            <AppCardHeader>
              <AppSectionHeader
                title="Content health"
                subtitle="Annual review reminders from each card's last reviewed date."
              />
            </AppCardHeader>
            <AppCardBody>
              <XStack flexWrap="wrap" gap="$sm">
                <AppCard backgroundColor="$background" flexBasis={0} flexGrow={1} minWidth={160}>
                  <AppCardBody>
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$hero" lineHeight="$hero">
                      {contentHealth.activeCards}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                      Active cards
                    </AppText>
                  </AppCardBody>
                </AppCard>
                <AppCard backgroundColor="$background" flexBasis={0} flexGrow={1} minWidth={160}>
                  <AppCardBody>
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$hero" lineHeight="$hero">
                      {contentHealth.dueNowCount}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                      Due now
                    </AppText>
                  </AppCardBody>
                </AppCard>
                <AppCard backgroundColor="$background" flexBasis={0} flexGrow={1} minWidth={160}>
                  <AppCardBody>
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$hero" lineHeight="$hero">
                      {contentHealth.dueSoonCount}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                      Due in 30 days
                    </AppText>
                  </AppCardBody>
                </AppCard>
                <AppCard backgroundColor="$background" flexBasis={0} flexGrow={1} minWidth={160}>
                  <AppCardBody>
                    <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$hero" lineHeight="$hero">
                      {contentHealth.missingReviewDateCount}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                      Missing review date
                    </AppText>
                  </AppCardBody>
                </AppCard>
              </XStack>

              {contentHealth.highlightCards.length === 0 ? (
                <AppEmptyState
                  title="No content cards yet"
                  body="Cards needing review will appear here once content starts building up."
                />
              ) : (
                <YStack gap="$sm">
                  <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                    {contentHealth.dueNowCount > 0 ? "Cards needing attention now" : "Next reviews coming up"}
                  </AppText>
                  {contentHealth.highlightCards.map((card) => (
                    <AppCard key={card.id} backgroundColor="$background">
                      <AppCardBody>
                        <XStack alignItems="center" gap="$sm">
                          <YStack flex={1} minWidth={0} gap={4}>
                            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                              {card.title}
                            </AppText>
                            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                              {card.category} ·{" "}
                              {contentHealth.dueNowCount > 0
                                ? describeContentHealthCard(card)
                                : formatContentHealthDate(card.dueDate)}
                            </AppText>
                          </YStack>
                          <AppButton type="button" tone="outline" size="sm" onPress={() => openHealthCard(card.id)}>
                            Edit
                          </AppButton>
                        </XStack>
                      </AppCardBody>
                    </AppCard>
                  ))}
                </YStack>
              )}
            </AppCardBody>
          </AppCard>

          <AppSection>
            <AppSectionHeader title="Support hub cards" subtitle="Manage the active help cards members see in Support." />
            {supportCards.length ? (
              <YStack gap="$sm">
                {supportCards.map((card) => (
                  <AppCard key={card.id}>
                    <AppCardBody>
                      <XStack alignItems="center" gap="$sm">
                        <YStack flex={1} minWidth={0} gap={4}>
                          <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                            {card.title}
                          </AppText>
                          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                            {card.category} · {getSupportCardCountryScopeLabel(card.country_scope)}
                          </AppText>
                        </YStack>
                        <AdminStatusPill tone={card.is_active ? "success" : "neutral"}>
                          {card.is_active ? "Active" : "Inactive"}
                        </AdminStatusPill>
                        <AppButton type="button" tone="outline" size="sm" onPress={() => toggleCardActive(card)}>
                          {card.is_active ? "Pause" : "Activate"}
                        </AppButton>
                        <AppButton type="button" tone="outline" size="sm" onPress={() => openEditCard(card)}>
                          Edit
                        </AppButton>
                      </XStack>
                    </AppCardBody>
                  </AppCard>
                ))}
              </YStack>
            ) : (
              <AppEmptyState
                title="No support cards yet"
                body="Add the first support card when you are ready."
                action={
                  <AppButton type="button" onPress={openNewCard}>
                    Add card
                  </AppButton>
                }
              />
            )}
          </AppSection>

          <AppSection>
            <AppSectionHeader title="Helper pack cards" subtitle="Keep the helper pack library current and easy to scan." />
            {helperCards.length ? (
              <YStack gap="$sm">
                {helperCards.map((card) => (
                  <AppCard key={card.id}>
                    <AppCardBody>
                      <XStack alignItems="center" gap="$sm">
                        <YStack flex={1} minWidth={0} gap={4}>
                          <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                            {card.title}
                          </AppText>
                          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                            {card.category} · {getSupportCardCountryScopeLabel(card.country_scope)}
                          </AppText>
                        </YStack>
                        <AdminStatusPill tone={card.is_active ? "success" : "neutral"}>
                          {card.is_active ? "Active" : "Inactive"}
                        </AdminStatusPill>
                        <AppButton type="button" tone="outline" size="sm" onPress={() => toggleCardActive(card)}>
                          {card.is_active ? "Pause" : "Activate"}
                        </AppButton>
                        <AppButton type="button" tone="outline" size="sm" onPress={() => openEditCard(card)}>
                          Edit
                        </AppButton>
                      </XStack>
                    </AppCardBody>
                  </AppCard>
                ))}
              </YStack>
            ) : (
              <AppEmptyState
                title="No helper pack cards yet"
                body="Helper pack cards will appear here once they have been created."
              />
            )}
          </AppSection>

          <AppSection>
            <AppSectionHeader
              title="Announcements"
              subtitle="One live notice at a time across web and mobile."
              action={
                <AppButton type="button" size="sm" onPress={openNewAnnouncement}>
                  Add announcement
                </AppButton>
              }
            />
            {announcements.length === 0 ? (
              <AppEmptyState
                title="No announcements yet"
                body="Create a banner or modal announcement when the app needs a temporary message."
              />
            ) : (
              <YStack gap="$sm">
                {announcements.map((announcement) => (
                  <AppCard key={announcement.id}>
                    <AppCardBody>
                      <XStack alignItems="center" gap="$sm">
                        <YStack flex={1} minWidth={0} gap={4}>
                          <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                            {announcement.title}
                          </AppText>
                          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                            {audienceLabel(announcement.audience_scope)} · {announcement.display_style}
                          </AppText>
                        </YStack>
                        <AdminStatusPill tone={announcement.is_active ? "success" : "neutral"}>
                          {announcement.is_active ? "Live" : "Inactive"}
                        </AdminStatusPill>
                        <AppButton
                          type="button"
                          tone="outline"
                          size="sm"
                          onPress={() => toggleAnnouncementActive(announcement)}
                        >
                          {announcement.is_active ? "Pause" : "Activate"}
                        </AppButton>
                        <AppButton
                          type="button"
                          tone="outline"
                          size="sm"
                          onPress={() => openEditAnnouncement(announcement)}
                        >
                          Edit
                        </AppButton>
                      </XStack>
                    </AppCardBody>
                  </AppCard>
                ))}
              </YStack>
            )}
          </AppSection>
        </>
      )}

      <AppDialogSurface
        open={!!editing}
        onOpenChange={(open) => !open && closeEditor()}
        title={
          editing?.type === "announcement"
            ? editing.value === "new"
              ? "Add announcement"
              : "Edit announcement"
            : editing?.value === "new"
              ? "Add card"
              : "Edit card"
        }
        maxWidthClassName="max-w-3xl"
      >
        <div className="max-h-[70vh] overflow-y-auto pr-1">

          {editing?.type === "announcement" ? (
            <YStack gap="$md" mt="$md">
              <AppFormSection>
                <AppFieldGroup label="Title">
                  <AppInput
                    value={announcementForm.title}
                    onChangeText={(value) => setAnnouncementForm((current) => ({ ...current, title: value }))}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Body">
                  <AppTextArea
                    value={announcementForm.body}
                    onChangeText={(value) => setAnnouncementForm((current) => ({ ...current, body: value }))}
                    rows={5}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Audience">
                  <AppButtonRow>
                    {audienceOptions.map((option) => (
                      <AppButton
                        key={option.value}
                        type="button"
                        tone={announcementForm.audience_scope === option.value ? "secondary" : "outline"}
                        size="sm"
                        onPress={() =>
                          setAnnouncementForm((current) => ({ ...current, audience_scope: option.value }))
                        }
                      >
                        {option.label}
                      </AppButton>
                    ))}
                  </AppButtonRow>
                </AppFieldGroup>
                <AppFieldGroup label="Display style">
                  <AppButtonRow>
                    {["banner", "modal"].map((style) => (
                      <AppButton
                        key={style}
                        type="button"
                        tone={announcementForm.display_style === style ? "secondary" : "outline"}
                        size="sm"
                        onPress={() => setAnnouncementForm((current) => ({ ...current, display_style: style }))}
                      >
                        {style === "banner" ? "Banner" : "Modal"}
                      </AppButton>
                    ))}
                  </AppButtonRow>
                </AppFieldGroup>
                <AppFieldGroup label="CTA label">
                  <AppInput
                    value={announcementForm.cta_label}
                    onChangeText={(value) => setAnnouncementForm((current) => ({ ...current, cta_label: value }))}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="CTA URL or route">
                  <AppInput
                    value={announcementForm.cta_url_or_route}
                    onChangeText={(value) =>
                      setAnnouncementForm((current) => ({ ...current, cta_url_or_route: value }))
                    }
                    placeholder="/activities or https://..."
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Starts at">
                  <AppInput
                    type="datetime-local"
                    value={announcementForm.starts_at}
                    onChangeText={(value) => setAnnouncementForm((current) => ({ ...current, starts_at: value }))}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Ends at">
                  <AppInput
                    type="datetime-local"
                    value={announcementForm.ends_at}
                    onChangeText={(value) => setAnnouncementForm((current) => ({ ...current, ends_at: value }))}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="State">
                  <AppButtonRow>
                    <AppButton
                      type="button"
                      tone={announcementForm.is_active ? "secondary" : "outline"}
                      size="sm"
                      onPress={() => setAnnouncementForm((current) => ({ ...current, is_active: true }))}
                    >
                      Active
                    </AppButton>
                    <AppButton
                      type="button"
                      tone={!announcementForm.is_active ? "secondary" : "outline"}
                      size="sm"
                      onPress={() => setAnnouncementForm((current) => ({ ...current, is_active: false }))}
                    >
                      Inactive
                    </AppButton>
                  </AppButtonRow>
                </AppFieldGroup>
                <AppFieldGroup label="Dismissible">
                  <AppButtonRow>
                    <AppButton
                      type="button"
                      tone={announcementForm.dismissible ? "secondary" : "outline"}
                      size="sm"
                      onPress={() => setAnnouncementForm((current) => ({ ...current, dismissible: true }))}
                    >
                      Dismissible
                    </AppButton>
                    <AppButton
                      type="button"
                      tone={!announcementForm.dismissible ? "secondary" : "outline"}
                      size="sm"
                      onPress={() => setAnnouncementForm((current) => ({ ...current, dismissible: false }))}
                    >
                      Locked
                    </AppButton>
                  </AppButtonRow>
                </AppFieldGroup>
                <AppButtonRow justifyContent="flex-end">
                  <AppButton type="button" tone="outline" onPress={closeEditor}>
                    Cancel
                  </AppButton>
                  <AppButton type="button" onPress={save}>
                    Save
                  </AppButton>
                </AppButtonRow>
              </AppFormSection>
            </YStack>
          ) : (
            <YStack gap="$md" mt="$md">
              <AppFormSection>
                <AppFieldGroup label="Section">
                  <AppInput
                    value={cardForm.section}
                    onChangeText={(value) => setCardForm((current) => ({ ...current, section: value }))}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Category">
                  <AppInput
                    value={cardForm.category}
                    onChangeText={(value) => setCardForm((current) => ({ ...current, category: value }))}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Country audience">
                  <AppButtonRow>
                    {SUPPORT_CARD_COUNTRY_SCOPE_OPTIONS.map((option) => (
                      <AppButton
                        key={option.value}
                        type="button"
                        tone={cardForm.country_scope === option.value ? "secondary" : "outline"}
                        size="sm"
                        onPress={() =>
                          setCardForm((current) => ({ ...current, country_scope: option.value }))
                        }
                      >
                        {option.label}
                      </AppButton>
                    ))}
                  </AppButtonRow>
                </AppFieldGroup>
                <AppFieldGroup label="Title">
                  <AppInput
                    value={cardForm.title}
                    onChangeText={(value) => setCardForm((current) => ({ ...current, title: value }))}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Body">
                  <AppTextArea
                    value={cardForm.body}
                    onChangeText={(value) => setCardForm((current) => ({ ...current, body: value }))}
                    rows={5}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Link URL">
                  <AppInput
                    value={cardForm.link_url}
                    onChangeText={(value) => setCardForm((current) => ({ ...current, link_url: value }))}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Sort order">
                  <AppInput
                    type="number"
                    value={String(cardForm.sort_order)}
                    onChangeText={(value) =>
                      setCardForm((current) => ({ ...current, sort_order: parseInt(value, 10) || 0 }))
                    }
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Last reviewed at">
                  <AppInput
                    type="date"
                    value={cardForm.last_reviewed_at}
                    onChangeText={(value) => setCardForm((current) => ({ ...current, last_reviewed_at: value }))}
                  />
                </AppFieldGroup>
                <AppFieldGroup label="State">
                  <AppButtonRow>
                    <AppButton
                      type="button"
                      tone={cardForm.is_active ? "secondary" : "outline"}
                      size="sm"
                      onPress={() => setCardForm((current) => ({ ...current, is_active: true }))}
                    >
                      Active
                    </AppButton>
                    <AppButton
                      type="button"
                      tone={!cardForm.is_active ? "secondary" : "outline"}
                      size="sm"
                      onPress={() => setCardForm((current) => ({ ...current, is_active: false }))}
                    >
                      Inactive
                    </AppButton>
                  </AppButtonRow>
                </AppFieldGroup>
                <AppButtonRow justifyContent="flex-end">
                  <AppButton type="button" tone="outline" onPress={closeEditor}>
                    Cancel
                  </AppButton>
                  <AppButton type="button" onPress={save}>
                    Save
                  </AppButton>
                </AppButtonRow>
              </AppFormSection>
            </YStack>
          )}
        </div>
      </AppDialogSurface>
    </AdminPageScaffold>
  );
};

export default AdminContent;
